import { JitsiMeeting } from "@jitsi/react-sdk";
import { X, Loader2 } from "lucide-react";

const JITSI_DOMAIN = process.env.REACT_APP_JITSI_DOMAIN || "meet.jit.si";

/**
 * Full-screen embedded Jitsi meeting. Renders nothing unless `room` is set.
 * Props:
 *  - room: Jitsi room name (from the online-class doc's `room_name`)
 *  - subject: heading label
 *  - displayName / email: participant identity
 *  - isHost: teacher/admin → given moderator-style config (first joiner = host)
 *  - onClose: called when the user leaves / closes
 */
export const JitsiRoomOverlay = ({ room, subject, displayName, email, isHost, onClose }) => {
  if (!room) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-slate-950 flex flex-col" data-testid="jitsi-overlay">
      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-900 border-b border-slate-800 shrink-0">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-white truncate" style={{ fontFamily: "Outfit" }}>
            {subject || "Online class"}
          </div>
          <div className="text-[11px] text-slate-400">{isHost ? "You are the host" : "Live class"} · {JITSI_DOMAIN}</div>
        </div>
        <button
          onClick={onClose}
          className="inline-flex items-center gap-1.5 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-xs font-semibold px-3.5 h-9 transition-colors shrink-0"
          data-testid="jitsi-leave-btn"
        >
          <X className="h-4 w-4" /> Leave
        </button>
      </div>
      <div className="flex-1 relative">
        <div className="absolute inset-0 flex items-center justify-center text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin" /> <span className="ml-2 text-sm">Connecting…</span>
        </div>
        <JitsiMeeting
          domain={JITSI_DOMAIN}
          roomName={room}
          userInfo={{ displayName: displayName || (isHost ? "Teacher" : "Student"), email: email || undefined }}
          configOverwrite={{
            prejoinPageEnabled: false,
            startWithAudioMuted: !isHost,
            startWithVideoMuted: false,
            disableModeratorIndicator: false,
          }}
          interfaceConfigOverwrite={{
            DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
            MOBILE_APP_PROMO: false,
          }}
          onApiReady={(api) => {
            if (displayName) api.executeCommand("displayName", displayName);
          }}
          onReadyToClose={() => onClose && onClose()}
          getIFrameRef={(node) => {
            node.style.height = "100%";
            node.style.width = "100%";
            node.style.position = "relative";
          }}
        />
      </div>
    </div>
  );
};

export default JitsiRoomOverlay;
